"use client"

import { createContext, useContext, useState, useEffect } from "react"
import axios from "axios"
import { getApiUrl, API_ENDPOINTS } from "../config/api"

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`
      fetchUser()
    } else {
      setLoading(false)
    }
  }, [])

  const normalizeUser = (data) => {
    // Accepts both /auth/me and /auth/login responses
    if (data.user) return { ...data.user }
    return { ...data }
  }

  const fetchUser = async () => {
    try {
      const response = await axios.get(getApiUrl(API_ENDPOINTS.AUTH.ME))
      setUser(normalizeUser(response.data))
    } catch (error) {
      console.error("Failed to fetch user:", error)
      localStorage.removeItem("token")
      delete axios.defaults.headers.common["Authorization"]
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.AUTH.LOGIN), {
      email,
      password,
    })
    const { access_token } = response.data
    localStorage.setItem("token", access_token)
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`
    // Fetch full user details from /auth/me after login
    const meResponse = await axios.get(getApiUrl(API_ENDPOINTS.AUTH.ME))
    setUser(normalizeUser(meResponse.data))
    return normalizeUser(meResponse.data)
  }

  const loginWithGoogle = async (credential) => {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.AUTH.GOOGLE), { credential })
    const { access_token } = response.data
    localStorage.setItem("token", access_token)
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`
    const meResponse = await axios.get(getApiUrl(API_ENDPOINTS.AUTH.ME))
    setUser(normalizeUser(meResponse.data))
    return normalizeUser(meResponse.data)
  }

  const register = async (email, password) => {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.AUTH.REGISTER), {
      email,
      password,
    })
    const { access_token } = response.data
    localStorage.setItem("token", access_token)
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`
    // Fetch full user details from /auth/me after register
    const meResponse = await axios.get(getApiUrl(API_ENDPOINTS.AUTH.ME))
    setUser(normalizeUser(meResponse.data))
    return normalizeUser(meResponse.data)
  }

  const logout = () => {
    localStorage.removeItem("token")
    delete axios.defaults.headers.common["Authorization"]
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const response = await axios.get(getApiUrl(API_ENDPOINTS.AUTH.ME))
      setUser(normalizeUser(response.data))
    } catch (error) {
      console.error("Failed to refresh user:", error)
    }
  }

  const forgotPassword = async (email) => {
    return axios.post(getApiUrl(API_ENDPOINTS.AUTH.FORGOT_PASSWORD), { email })
  }

  const verifyResetCode = async (email, code) => {
    return axios.post(getApiUrl(API_ENDPOINTS.AUTH.VERIFY_RESET_CODE), { email, code })
  }

  const resetPassword = async (email, code, new_password) => {
    return axios.post(getApiUrl(API_ENDPOINTS.AUTH.RESET_PASSWORD), { email, code, new_password })
  }

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    refreshUser,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    loginWithGoogle,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
